import PropTypes from 'prop-types';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import { voolistExtension } from 'src/utils/voolist-extension';

import { getBulkEditStatus, getPendingPlatformSyncsCount } from 'src/redux/slices/listing';

import Iconify from 'src/components/iconify';

import { FINAL_STATUSES, RESULTS_UNAVAILABLE } from './bulk-job-progress';

const UPDATING_MARKETPLACES = 'Updating your marketplaces…';
// For a seller whose browser here has no Voolist extension (a phone, say): what the marketplaces only Chrome updates
// are waiting for.
const WAITING_FOR_CHROME =
  'Your changes are saved. Some marketplaces get them the next time you open Chrome on your computer.';

// Poll interval in milliseconds
const POLL_INTERVAL = 2000;
const PLATFORM_SYNC_POLL_INTERVAL = 3000;
// How long the card watches the browser send the changes to the marketplaces it updates. After that the
// changes may still be on their way, so the card stops watching without saying they arrived.
const PLATFORM_SYNC_TIMEOUT = 120000;

/**
 * What the card knows about the changes that only Chrome with the Voolist extension sends to marketplaces (the ones
 * Voolist can't update from its server):
 *   idle        - there are none to send
 *   checking    - finding out whether this browser has the extension
 *   syncing     - this browser is sending them
 *   done        - this browser has sent them all
 *   unconfirmed - the card stopped watching before this browser said it was done
 *   waiting     - this browser has no extension, so they wait for Chrome with the extension to be open
 */
const PLATFORM_SYNC = {
  IDLE: 'idle',
  CHECKING: 'checking',
  SYNCING: 'syncing',
  DONE: 'done',
  UNCONFIRMED: 'unconfirmed',
  WAITING: 'waiting',
};

// What a listing's line says about its changes that only Chrome sends, at each point. Nothing while the card is still
// finding out whether this browser can send them.
const CHROME_CHANGES_CHIP = {
  [PLATFORM_SYNC.SYNCING]: { label: 'Sending to marketplaces…', variant: 'outlined' },
  [PLATFORM_SYNC.DONE]: { label: 'Sent to marketplaces', variant: 'filled' },
  [PLATFORM_SYNC.UNCONFIRMED]: { label: 'Still updating', variant: 'outlined' },
  [PLATFORM_SYNC.WAITING]: { label: 'Waiting for Chrome', variant: 'outlined' },
};

/**
 * The sentences a saved listing's line carries about marketplaces that didn't take the change: one that left its
 * post as it was while reviewing it, and the server's word on any marketplace that turned the change down or couldn't
 * be sent it.
 */
function notUpdatedNotes (result) {
  return [...(result.notices || []), ...(result.syncWarning ? [result.syncWarning] : [])];
}

/** Every sentence a saved listing's line carries: the marketplaces that didn't take the change, and any note to check it. */
function lineNotes (result) {
  return [...notUpdatedNotes(result), ...(result.checkListingNote ? [result.checkListingNote] : [])];
}

export default function BulkEditProgress({ batchId, onClose }) {
  const [status, setStatus] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState(null);
  const syncTriggeredRef = useRef(false);
  const [platformSyncPhase, setPlatformSyncPhase] = useState(PLATFORM_SYNC.IDLE);

  const isFinal = FINAL_STATUSES.includes(status?.status);
  // The server no longer has this run (its results are kept for an hour). The run may well have worked.
  const isResultsGone = status?.status === 'not_found';
  const isUpdatingMarketplaces =
    platformSyncPhase === PLATFORM_SYNC.CHECKING || platformSyncPhase === PLATFORM_SYNC.SYNCING;

  // Fetch batch status
  const fetchStatus = useCallback(async () => {
    if (!batchId) return;

    try {
      const response = await getBulkEditStatus(batchId);
      if (response) {
        setStatus(response);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch bulk edit status:', err);
      setError(err.message || "Couldn't check progress right now. Your changes are still being saved.");
    }
  }, [batchId]);

  // Ask straight away, then keep asking until the run reaches a final state. Nothing is fetched after that: the
  // status call records the run's completion, so a repeat would count it twice.
  useEffect(() => {
    if (!batchId || isFinal) return undefined;

    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [batchId, fetchStatus, isFinal]);

  // Once the run is done, the changes only Chrome sends go out from this browser when it has the extension.
  // Otherwise (a phone, or a browser without it) they wait for Chrome with the extension to be open.
  useEffect(() => {
    if (!status || status.status !== 'completed' || syncTriggeredRef.current) return undefined;
    const hasPendingExtension = status.results?.some(r => r.syncStatus === 'pending_extension');
    if (!hasPendingExtension) return undefined;

    syncTriggeredRef.current = true;
    setPlatformSyncPhase(PLATFORM_SYNC.CHECKING);
    let isCurrent = true;
    voolistExtension.ping().then(({ installed }) => {
      if (!isCurrent) return;
      if (!installed) {
        setPlatformSyncPhase(PLATFORM_SYNC.WAITING);
        return;
      }
      setPlatformSyncPhase(PLATFORM_SYNC.SYNCING);
      voolistExtension.triggerPendingPlatformSyncs().catch(() => {});
    });
    return () => {
      isCurrent = false;
    };
  }, [status]);

  // Poll backend to detect when extension platform syncs are done
  useEffect(() => {
    if (platformSyncPhase !== PLATFORM_SYNC.SYNCING) return undefined;

    const startTime = Date.now();

    const interval = setInterval(async () => {
      if (Date.now() - startTime > PLATFORM_SYNC_TIMEOUT) {
        setPlatformSyncPhase(PLATFORM_SYNC.UNCONFIRMED);
        return;
      }

      try {
        const count = await getPendingPlatformSyncsCount();
        if (count === 0) {
          setPlatformSyncPhase(PLATFORM_SYNC.DONE);
        }
      } catch {
        // A check that couldn't be made says nothing either way: keep watching until the time runs out.
      }
    }, PLATFORM_SYNC_POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [platformSyncPhase]);

  // Reset all per-batch state when a new batchId arrives.
  // Same rationale as BulkCrossListProgress: this component stays mounted globally and only
  // the batchId prop changes between bulk edit batches. Without this, syncTriggeredRef stays
  // `true` from the previous batch and triggerPendingPlatformSyncs is never called.
  // (Bulk edit is partially self-healing via the extension's resumePendingPlatformSyncs --
  // unlike bulk crosslist 'create' which is intentionally skipped -- but the UX glitch is
  // the same and the fix is identical.)
  useEffect(() => {
    setIsDismissed(false);
    setStatus(null);
    setError(null);
    setPlatformSyncPhase(PLATFORM_SYNC.IDLE);
    syncTriggeredRef.current = false;
  }, [batchId]);

  // A dismissed card comes back once the run and its marketplace updates are over, so the results are seen.
  useEffect(() => {
    if (isDismissed && isFinal && !isUpdatingMarketplaces) {
      setIsDismissed(false);
    }
  }, [isDismissed, isFinal, isUpdatingMarketplaces]);

  // Don't render if no batchId
  if (!batchId) return null;

  const isCompleted = status?.status === 'completed';
  const isProcessing = status?.status === 'processing' || status?.status === 'queued';
  const isSyncingPlatforms = platformSyncPhase === PLATFORM_SYNC.SYNCING;
  const isSyncUnconfirmed = platformSyncPhase === PLATFORM_SYNC.UNCONFIRMED;
  const isWaitingForChrome = platformSyncPhase === PLATFORM_SYNC.WAITING;
  const progress = status?.progress || 0;
  const totalListings = status?.total || 0;
  const completedListings = status?.completed || 0;
  const failedCount = status?.failed || 0;
  const successCount = status?.successCount || 0;
  const results = status?.results || [];
  // Listings saved that some marketplace didn't take the change for, each line saying which and why.
  const hasNotices = results.some((result) => result.success && notUpdatedNotes(result).length > 0);
  // Saved, while some marketplaces may not have the changes yet.
  const isSavedNotYetSent = isSyncUnconfirmed || isWaitingForChrome;

  // Get status icon and color
  const getStatusDisplay = () => {
    if (isResultsGone) {
      return { icon: 'solar:danger-triangle-bold', color: 'warning.main', text: 'Results not available' };
    }
    if (isCompleted && isUpdatingMarketplaces) {
      return { icon: 'solar:refresh-bold', color: 'info.main', text: UPDATING_MARKETPLACES };
    }
    if (isCompleted) {
      if (failedCount > 0) {
        return { icon: 'solar:danger-triangle-bold', color: 'warning.main', text: 'Done, some weren’t updated' };
      }
      if (isSavedNotYetSent) {
        return { icon: 'solar:clock-circle-bold', color: 'info.main', text: 'Saved in Voolist' };
      }
      return { icon: 'solar:check-circle-bold', color: 'success.main', text: 'Done' };
    }
    if (isProcessing) {
      return { icon: 'solar:refresh-bold', color: 'info.main', text: 'Saving your changes…' };
    }
    return { icon: 'solar:clock-circle-bold', color: 'text.secondary', text: 'Starting…' };
  };

  const statusDisplay = getStatusDisplay();

  // Get background color based on status
  const getBgColor = () => {
    if (isResultsGone) return 'warning.lighter';
    if (!isCompleted || isUpdatingMarketplaces) return 'info.lighter';
    if (failedCount > 0) return 'warning.lighter';
    return isSavedNotYetSent ? 'info.lighter' : 'success.lighter';
  };

  // Get chip color
  const getChipColor = () => {
    if (isResultsGone) return 'warning';
    if (isUpdatingMarketplaces) return 'info';
    if (isCompleted && failedCount > 0) return 'warning';
    if (isCompleted && !isSavedNotYetSent) return 'success';
    return 'info';
  };

  // Get per-result sync chip for a given result
  const getSyncChip = (result) => {
    if (!result.success) return null;

    if (result.syncStatus === 'pending_extension') {
      const chip = CHROME_CHANGES_CHIP[platformSyncPhase];
      return chip ? (
        <Chip label={chip.label} size="small" color="info" variant={chip.variant} sx={{ height: 18, fontSize: 10 }} />
      ) : null;
    }

    if (result.syncStatus === 'completed') {
      return <Chip label="Sent to marketplaces" size="small" color="info" sx={{ height: 18, fontSize: 10 }} />;
    }

    if (result.syncStatus === 'failed') {
      return <Chip label="Not updated on every marketplace" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />;
    }

    return null;
  };

  const handleDismissOrClose = () => {
    if (isFinal && !isUpdatingMarketplaces) {
      onClose();
    } else {
      setIsDismissed(true);
    }
  };

  // Don't render if dismissed (will reappear when job completes)
  if (isDismissed) return null;

  // Minimized view
  if (isMinimized) {
    return (
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1300,
            borderRadius: 2,
            overflow: 'hidden',
            minWidth: 280,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: getBgColor(),
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            {(isProcessing || isUpdatingMarketplaces) ? (
              <CircularProgress size={20} color="info" />
            ) : (
              <Iconify icon={statusDisplay.icon} sx={{ color: statusDisplay.color }} />
            )}

            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {(() => {
                if (isResultsGone) return 'Bulk Edit results not available';
                if (isUpdatingMarketplaces) return UPDATING_MARKETPLACES;
                return `Bulk Edit: ${isCompleted ? `${successCount}/${totalListings}` : `${progress}%`}`;
              })()}
            </Typography>

            <IconButton size="small" aria-label="Expand" onClick={() => setIsMinimized(false)}>
              <Iconify icon="eva:expand-outline" width={18} />
            </IconButton>

            <IconButton size="small" aria-label="Close" onClick={handleDismissOrClose}>
              <Iconify icon="eva:close-fill" width={18} />
            </IconButton>
          </Box>

          {(isProcessing || isUpdatingMarketplaces) && (
            <LinearProgress
              variant={isUpdatingMarketplaces ? 'indeterminate' : 'determinate'}
              value={isUpdatingMarketplaces ? undefined : progress}
              sx={{ height: 3 }}
            />
          )}
        </Paper>
      </Slide>
    );
  }

  // Full view
  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1300,
          borderRadius: 2,
          overflow: 'hidden',
          minWidth: 400,
          maxWidth: 480,
        }}
      >
        <Card sx={{ bgcolor: getBgColor() }}>
          <CardContent sx={{ p: 2.5 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Stack direction="row" alignItems="center" gap={1}>
                {(isProcessing || isUpdatingMarketplaces) ? (
                  <CircularProgress size={20} color="info" />
                ) : (
                  <Iconify icon={statusDisplay.icon} width={24} sx={{ color: statusDisplay.color }} />
                )}
                <Typography variant="subtitle1">
                  Bulk Edit
                </Typography>
                <Chip
                  label={statusDisplay.text}
                  size="small"
                  color={getChipColor()}
                />
              </Stack>

              <Stack direction="row" gap={0.5}>
                <IconButton size="small" aria-label="Minimize" onClick={() => setIsMinimized(true)}>
                  <Iconify icon="eva:minus-outline" width={18} />
                </IconButton>
                <IconButton size="small" aria-label="Close" onClick={handleDismissOrClose}>
                  <Iconify icon="eva:close-fill" width={18} />
                </IconButton>
              </Stack>
            </Stack>

            {/* Progress bar */}
            {isProcessing && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Updating your listings…
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {completedListings}/{totalListings}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            )}

            {/* Platform sync indeterminate progress */}
            {isSyncingPlatforms && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Updating your marketplaces. Keep Chrome open until it&apos;s done.
                </Typography>
                <LinearProgress
                  variant="indeterminate"
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            )}

            {/* Results summary */}
            {isCompleted && (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={2}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Iconify icon="solar:check-circle-bold" sx={{ color: 'success.main' }} />
                      <Typography variant="body2">
                        {successCount} updated
                      </Typography>
                    </Stack>

                    {failedCount > 0 && (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <Iconify icon="solar:close-circle-bold" sx={{ color: 'error.main' }} />
                        <Typography variant="body2">
                          {failedCount} failed
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {results.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => setShowDetails(!showDetails)}
                      sx={{ p: 0.5 }}
                    >
                      <Iconify
                        icon={showDetails ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                        width={20}
                      />
                    </IconButton>
                  )}
                </Stack>

                {/* Individual listing results */}
                <Collapse in={showDetails}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {results.map((result, index) => (
                        <ListItem
                          key={result.listingId || index}
                          disablePadding
                          sx={{ py: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            {result.success ? (
                              <Iconify
                                icon="solar:check-circle-bold"
                                width={18}
                                sx={{ color: 'success.main' }}
                              />
                            ) : (
                              <Iconify
                                icon="solar:close-circle-bold"
                                width={18}
                                sx={{ color: 'error.main' }}
                              />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            secondaryTypographyProps={{ component: 'div' }}
                            primary={
                              <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                                {result.listingTitle || 'Untitled item'}
                              </Typography>
                            }
                            secondary={
                              result.success ? (
                                <>
                                  <Stack direction="row" alignItems="center" gap={0.5}>
                                    <Typography variant="caption" color="success.main">
                                      Updated
                                    </Typography>
                                    {getSyncChip(result)}
                                  </Stack>
                                  {lineNotes(result).map((note) => (
                                    <Typography key={note} variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                      {note}
                                    </Typography>
                                  ))}
                                </>
                              ) : (
                                <Typography variant="caption" color="error.main" noWrap>
                                  {result.error || 'Not updated'}
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Collapse>

                {failedCount > 0 && !showDetails && (
                  <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                    Some listings weren&apos;t updated. Click the arrow to see why.
                  </Alert>
                )}

                {hasNotices && !showDetails && (
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    Some listings weren&apos;t updated on every marketplace. Click the arrow to see why.
                  </Alert>
                )}

                {isSyncUnconfirmed && (
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    Some marketplaces are still updating. Keep Chrome open, and check My Listings in a few minutes.
                  </Alert>
                )}

                {isWaitingForChrome && (
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    {WAITING_FOR_CHROME}
                  </Alert>
                )}
              </Stack>
            )}

            {isResultsGone && (
              <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                {status.message || RESULTS_UNAVAILABLE}
              </Alert>
            )}

            {/* Error display */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Paper>
    </Slide>
  );
}

BulkEditProgress.propTypes = {
  batchId: PropTypes.string,
  onClose: PropTypes.func,
};
